/**
 * Formats a string or number to contain only integer characters.
 * Allows negative sign at the beginning.
 * Preserves the input type (returns number if input is number, string if input is string).
 *
 * @param {string|number} input - The value to format
 * @returns {string|number} - Formatted value in the same type as input
 *
 * @example
 * formatInteger('abc-123def456-') // returns '-123456'
 * formatInteger('123') // returns '123'
 * formatInteger('-456') // returns '-456'
 * formatInteger(123) // returns 123
 * formatInteger(-456) // returns -456
 */
export function formatInteger(input) {
  const isNumber = typeof input === 'number';
  if (input === null || input === undefined || input === '') {
    return isNumber ? 0 : '';
  }

  const text = input.toString();

  // Remove all non-numeric characters except minus
  const filtered = text.replace(/[^\d-]/g, '');

  // Ensure minus sign only at the beginning
  let result;
  if (filtered.includes('-')) {
    result = (filtered[0] === '-' ? '-' : '') + filtered.replace(/-/g, '');
  } else {
    result = filtered;
  }

  // Return same type as input
  return isNumber && result !== '' && result !== '-' ? Number(result) : result;
}

/**
 * Formats a string or number to contain only non-negative integer characters.
 * Strips all non-numeric characters including minus signs.
 * Preserves the input type (returns number if input is number, string if input is string).
 *
 * @param {string|number} input - The value to format
 * @returns {string|number} - Formatted value in the same type as input
 *
 * @example
 * formatNonNegativeInteger('abc123def456') // returns '123456'
 * formatNonNegativeInteger('-123') // returns '123'
 * formatNonNegativeInteger('45.67') // returns '4567'
 * formatNonNegativeInteger(123) // returns 123
 * formatNonNegativeInteger(-456) // returns 456
 */
export function formatNonNegativeInteger(input) {
  const isNumber = typeof input === 'number';
  if (input === null || input === undefined || input === '') {
    return isNumber ? 0 : '';
  }

  const result = input.toString().replace(/[^\d]/g, '');

  // Return same type as input
  return isNumber && result !== '' ? Number(result) : result;
}
